import { CheckOutlined, LoadingOutlined } from "@ant-design/icons";
import { Button } from "antd";
import type React from "react";
import { useIsMobileView } from "../../hooks/ui-hook";
import AppointmentSelectionModal from "./AppointmentSelectionModal";
import { useTransfer } from "./TransferContext";

interface TransferToEHRButtonProps {
  className?: string;
  icon?: React.ReactNode;
}

const ACTIVE_STATUSES = ["pending", "logging_in", "patient_lookup", "template_mapping", "writing_note"];

const TransferToEHRButton: React.FC<TransferToEHRButtonProps> = ({ className = "", icon: defaultIcon }) => {
  const isMobile = useIsMobileView();
  const {
    isLoading,
    credentialStatus,
    currentStatus,
    appointmentOptions,
    showAppointmentModal,
    isSelectingAppointment,
    startTransfer,
    selectAppointment,
    cancelAppointment,
  } = useTransfer();

  if (isMobile) return null;

  // Compute button state
  const isInProgress = ACTIVE_STATUSES.includes(currentStatus);
  const isAwaitingAppointment = currentStatus === "awaiting_appointment";
  const isCompleted = currentStatus === "completed";
  const isFailed = currentStatus === "failed";
  const hasCredentials = credentialStatus?.has_credential ?? false;
  const isDisabled = isLoading || isInProgress || isCompleted || isAwaitingAppointment;

  // Compute button text and icon
  let buttonText = "Transfer to EHR";
  let icon = null;

  // Determine if we're in a "processing" visual state that should maintain primary styling
  const isProcessingState = isLoading || isInProgress || isAwaitingAppointment;
  const style = { color: "white" };
  if (isLoading) {
    buttonText = "Checking...";
    icon = <LoadingOutlined style={style} />;
  } else if (!hasCredentials) {
    buttonText = "Transfer to EHR";
  } else if (isInProgress) {
    buttonText = "Transferring...";
    icon = <LoadingOutlined style={style} />;
  } else if (isAwaitingAppointment) {
    buttonText = "Select Appointment...";
    icon = <LoadingOutlined style={style} />;
  } else if (isCompleted) {
    buttonText = "Transferred";
    icon = <CheckOutlined style={style} />;
  } else if (isFailed) {
    buttonText = "Retry Transfer";
  } else if (hasCredentials) {
    buttonText = "Transfer to EHR";
  }

  return (
    <>
      <Button
        type="primary"
        className={className}
        style={{
          display: "flex",
          alignItems: "center",
          ...((isProcessingState || isCompleted) && {
            backgroundColor: "#1677ff",
            borderColor: "#1677ff",
            color: "white",
            opacity: 1,
          }),
        }}
        onClick={startTransfer}
        disabled={isDisabled}
        icon={icon ?? defaultIcon}
      >
        {buttonText}
      </Button>

      <AppointmentSelectionModal
        visible={showAppointmentModal}
        appointments={appointmentOptions}
        onSelect={selectAppointment}
        onCancel={cancelAppointment}
        loading={isSelectingAppointment}
      />
    </>
  );
};

export default TransferToEHRButton;
